import React from "react"
import cileaLogoWhite from "../../assets/img/cilea_logo_white.png"
import englishFlag from "../../assets/img/english.png"
import franceFlag from "../../assets/img/france.png"
import italyFlag from "../../assets/img/italy.png"
import spainFlag from "../../assets/img/spain.png"
import {
    Badge,
    Box,
    List,
    Collapse,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Divider,
    Avatar,
    Typography,
    Tooltip,
    Select,
    MenuItem,
} from "@mui/material"
import { useTranslation } from "react-i18next"
import LogoutIcon from "@mui/icons-material/Logout"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import { useLocation, useNavigate } from "react-router-dom"
import {
    getNavItemsForRole,
    getPrimaryRole,
    NavItem,
} from "../../services/navigationConfig"
import { useAuth } from "../../services/AuthContext"
import apiClient from "../../services/apiClient"
import { getStockAlertsSummary } from "../../services/itemsService"

interface MainNavProps {
    onNavigate?: () => void
    collapsed?: boolean
}

const NAV_TRANSLATION_KEYS: Record<string, string> = {
    "/superadmin/entities": "nav.items.superadminEntities",
    "/superadmin/administration": "nav.items.superadminAdministration",
    "/home": "nav.items.home",
    "/pos": "nav.items.pos",
    "/admin/customers": "nav.items.customers",
    "/exports-documents": "nav.items.exportsDocuments",
    "/admin/items": "nav.items.items",
    "/admin/categories": "nav.items.categories",
    "/admin/users": "nav.items.users",
    "/stats": "nav.items.stats",
    "/admin/settings": "nav.items.settings",
}

const MainNav: React.FC<MainNavProps> = ({ onNavigate, collapsed = false }) => {
    const location = useLocation()
    const navigate = useNavigate()
    const { user, logout } = useAuth()
    const { i18n, t } = useTranslation()

    const currentPath = location.pathname
    const primaryRole = getPrimaryRole(user?.roles)
    const filteredNavItems = React.useMemo(
        () => getNavItemsForRole(primaryRole),
        [primaryRole]
    )
    const [expandedParents, setExpandedParents] = React.useState<
        Record<string, boolean>
    >({})
    const [stockAlertsCount, setStockAlertsCount] = React.useState(0)

    React.useEffect(() => {
        let cancelled = false

        ;(async () => {
            try {
                const companiesResponse = await apiClient.get("/api/companies")
                if (cancelled) return

                const companyId = companiesResponse?.companies?.[0]?.id
                if (!companyId) {
                    setStockAlertsCount(0)
                    return
                }

                const summary = await getStockAlertsSummary(companyId)
                if (cancelled) return

                setStockAlertsCount(Number(summary?.count || 0))
            } catch (error) {
                if (!cancelled) {
                    setStockAlertsCount(0)
                }
            }
        })()

        return () => {
            cancelled = true
        }
    }, [currentPath])

    const isPathMatch = React.useCallback(
        (targetPath: string) => {
            return (
                currentPath === targetPath ||
                currentPath.startsWith(`${targetPath}/`)
            )
        },
        [currentPath]
    )

    const hasActiveChild = React.useCallback(
        (item: NavItem): boolean => {
            if (!item.children || item.children.length === 0) return false
            return item.children.some((child) => isPathMatch(child.path))
        },
        [isPathMatch]
    )

    React.useEffect(() => {
        const nextState: Record<string, boolean> = {}
        filteredNavItems.forEach((item) => {
            if (
                item.children &&
                item.children.length > 0 &&
                hasActiveChild(item)
            ) {
                nextState[item.path] = true
            }
        })

        if (Object.keys(nextState).length === 0) return

        setExpandedParents((prev) => {
            const updated = { ...prev }
            let changed = false

            Object.entries(nextState).forEach(([path, expanded]) => {
                if (updated[path] !== expanded) {
                    updated[path] = expanded
                    changed = true
                }
            })

            return changed ? updated : prev
        })
    }, [filteredNavItems, hasActiveChild])

    const handleNavClick = (path: string) => {
        navigate(path)
        onNavigate?.()
    }

    const handleLogout = async () => {
        await logout()
        navigate("/login")
    }

    const userInitials = user?.display_name
        ? user.display_name
              .split(" ")
              .map((n: string) => n[0])
              .join("")
              .toUpperCase()
              .slice(0, 2)
        : "?"

    const navItemSx = (active: boolean) => ({
        borderRadius: "10px",
        mb: 0.5,
        color: "white",
        backgroundColor: active ? "rgba(185, 28, 28, 0.8)" : "transparent",
        "&:hover": {
            backgroundColor: active
                ? "rgba(185, 28, 28, 0.9)"
                : "rgba(255, 255, 255, 0.1)",
        },
        transition: "background-color 0.2s",
    })

    const getNavLabel = React.useCallback(
        (item: NavItem): string => {
            const key = NAV_TRANSLATION_KEYS[item.path]
            if (!key) return item.label
            return t(key, { defaultValue: item.label })
        },
        [t]
    )

    const renderNavItem = (item: NavItem, depth = 0) => {
        const Icon = item.icon
        const translatedLabel = getNavLabel(item)
        const childActive = hasActiveChild(item)
        const selfActive = isPathMatch(item.path)
        const isActive = selfActive || childActive
        const hasChildren = !!item.children && item.children.length > 0
        const isExpanded = !!expandedParents[item.path] || childActive
        const showStockAlertBadge =
            item.path === "/admin/items" && stockAlertsCount > 0

        const handleClick = () => {
            if (hasChildren) {
                setExpandedParents((prev) => ({
                    ...prev,
                    [item.path]: !isExpanded,
                }))
                handleNavClick(item.path)
                return
            }

            handleNavClick(item.path)
        }

        const button = (
            <ListItemButton
                key={item.path}
                onClick={handleClick}
                aria-current={!hasChildren && isActive ? "page" : undefined}
                sx={{
                    ...navItemSx(isActive),
                    justifyContent: collapsed ? "center" : "flex-start",
                    px: collapsed ? 1 : depth === 0 ? 2 : 2.5,
                    py: depth > 0 ? 0.7 : 1,
                }}
            >
                <ListItemIcon
                    sx={{
                        color: "inherit",
                        minWidth: collapsed ? 0 : 36,
                        justifyContent: "center",
                    }}
                >
                    <Badge
                        color="warning"
                        badgeContent={
                            showStockAlertBadge ? stockAlertsCount : 0
                        }
                        max={99}
                        invisible={!showStockAlertBadge}
                        sx={{
                            "& .MuiBadge-badge": {
                                fontSize: "0.65rem",
                                minWidth: 18,
                                height: 18,
                            },
                        }}
                    >
                        <Icon sx={{ fontSize: depth > 0 ? 18 : 20 }} />
                    </Badge>
                </ListItemIcon>
                {!collapsed && (
                    <>
                        <ListItemText
                            primary={translatedLabel}
                            primaryTypographyProps={{
                                fontSize: depth > 0 ? "0.8rem" : "0.85rem",
                                fontWeight: isActive ? 600 : 500,
                            }}
                        />
                        {hasChildren &&
                            (isExpanded ? (
                                <ExpandMoreIcon />
                            ) : (
                                <ChevronRightIcon />
                            ))}
                    </>
                )}
            </ListItemButton>
        )

        return (
            <React.Fragment key={item.path}>
                {collapsed ? (
                    <Tooltip title={translatedLabel} placement="right" arrow>
                        {button}
                    </Tooltip>
                ) : (
                    button
                )}

                {hasChildren && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <List disablePadding>
                            {item.children!.map((child) =>
                                renderNavItem(child, depth + 1)
                            )}
                        </List>
                    </Collapse>
                )}
            </React.Fragment>
        )
    }

    return (
        <Box
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
            }}
        >
            {/* Logo */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: collapsed ? "center" : "flex-start",
                    gap: collapsed ? 0 : 1.5,
                    px: collapsed ? 1 : 2,
                    py: 2.5,
                    flexShrink: 0,
                }}
            >
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    <img
                        src={cileaLogoWhite}
                        alt="Cilea"
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                        }}
                    />
                </Box>
                {!collapsed && (
                    <Typography
                        sx={{
                            color: "white",
                            fontWeight: 700,
                            fontSize: "1.1rem",
                            letterSpacing: "0.02em",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {t("nav.brand")}
                    </Typography>
                )}
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.15)", mx: 2 }} />

            {/* Nav items */}
            <List
                sx={{
                    flex: 1,
                    overflow: "auto",
                    px: collapsed ? 0.5 : 1.5,
                    py: 1,
                }}
            >
                {filteredNavItems.map((item) => renderNavItem(item))}
            </List>

            {/* Language picker */}
            <Divider
                sx={{
                    borderColor: "rgba(255,255,255,0.15)",
                    mx: collapsed ? 0.5 : 2,
                }}
            />
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: collapsed ? 0.5 : 2,
                    py: 1,
                    flexShrink: 0,
                }}
            >
                <Select
                    value={i18n.language?.split("-")[0] || "fr"}
                    onChange={(e) =>
                        i18n.changeLanguage(e.target.value as string)
                    }
                    variant="standard"
                    disableUnderline
                    sx={{
                        color: "white",
                        fontSize: "1rem",
                        fontWeight: 600,
                        minWidth: collapsed ? 36 : 100,
                        textAlign: "center",
                        "& .MuiSelect-icon": { color: "rgba(255,255,255,0.7)" },
                        "& .MuiSelect-select": {
                            py: 0.5,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        },
                    }}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                bgcolor: "#1e1e2f",
                                color: "white",
                                "& .MuiMenuItem-root:hover": {
                                    bgcolor: "rgba(185, 28, 28, 0.3)",
                                },
                                "& .Mui-selected": {
                                    bgcolor:
                                        "rgba(185, 28, 28, 0.5) !important",
                                },
                            },
                        },
                    }}
                >
                    <MenuItem value="fr">
                        <Box
                            component="img"
                            src={franceFlag}
                            alt="Français"
                            sx={{
                                mr: collapsed ? 0 : 1,
                                width: 24,
                                height: 24,
                                objectFit: "contain",
                            }}
                        />
                        {!collapsed && "Français"}
                    </MenuItem>
                    <MenuItem value="en">
                        <Box
                            component="img"
                            src={englishFlag}
                            alt="English"
                            sx={{
                                mr: collapsed ? 0 : 1,
                                width: 24,
                                height: 24,
                                objectFit: "contain",
                            }}
                        />
                        {!collapsed && "English"}
                    </MenuItem>
                    <MenuItem value="it">
                        <Box
                            component="img"
                            src={italyFlag}
                            alt="Italiano"
                            sx={{
                                mr: collapsed ? 0 : 1,
                                width: 24,
                                height: 24,
                                objectFit: "contain",
                            }}
                        />
                        {!collapsed && "Italiano"}
                    </MenuItem>
                    <MenuItem value="es">
                        <Box
                            component="img"
                            src={spainFlag}
                            alt="Español"
                            sx={{
                                mr: collapsed ? 0 : 1,
                                width: 24,
                                height: 24,
                                objectFit: "contain",
                            }}
                        />
                        {!collapsed && "Español"}
                    </MenuItem>
                </Select>
            </Box>

            {/* Bottom: user info + logout */}
            <Divider
                sx={{
                    borderColor: "rgba(255,255,255,0.15)",
                    mx: collapsed ? 0.5 : 2,
                }}
            />
            <Box
                sx={{
                    display: "flex",
                    flexDirection: collapsed ? "column" : "row",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: collapsed ? 0.5 : 1.5,
                    px: collapsed ? 0.5 : 2,
                    py: 2,
                    flexShrink: 0,
                }}
            >
                <Tooltip
                    title={
                        collapsed
                            ? user?.display_name || t("nav.user.default")
                            : ""
                    }
                    placement="right"
                    arrow
                >
                    <Avatar
                        sx={{
                            width: 36,
                            height: 36,
                            backgroundColor: "#b91c1c",
                            color: "white",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                        }}
                    >
                        {userInitials}
                    </Avatar>
                </Tooltip>
                {!collapsed && (
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            sx={{
                                color: "white",
                                fontWeight: 600,
                                fontSize: "0.8rem",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {user?.display_name || t("nav.user.default")}
                        </Typography>
                    </Box>
                )}
                <Tooltip
                    title={collapsed ? t("nav.logout") : ""}
                    placement="right"
                    arrow
                >
                    <ListItemButton
                        onClick={handleLogout}
                        sx={{
                            width: 36,
                            height: 36,
                            minWidth: 36,
                            p: 0,
                            borderRadius: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "rgba(255,255,255,0.7)",
                            "&:hover": {
                                color: "white",
                                backgroundColor: "rgba(255,255,255,0.1)",
                            },
                        }}
                    >
                        <LogoutIcon sx={{ fontSize: 18 }} />
                    </ListItemButton>
                </Tooltip>
            </Box>
        </Box>
    )
}

export default MainNav
